<template>
  <DataSourceReauthenticateDialog
    v-if="reauthDatasource"
    v-model:visibility="reauthDatasourceVisible"
    :data-source="reauthDatasource"
    :close="true"
    @authentication-completed="recheckAuthentication()" />

  <div
    style="display: flex; align-items: center; justify-content: space-between">
    <div style="color: var(--el-text-color-regular)">
      {{ i18n.t('groupAddMicrosoftForm.synchronizeData') }}
    </div>
    <SmTooltip placement="top">
      <template #content>
        {{ i18n.t('groupAddMicrosoftForm.tooltip') }}
      </template>
      <SmSwitch v-model="groupSyncActive" />
    </SmTooltip>
  </div>
  <transition name="expand">
    <div v-if="groupSyncActive">
      <SmDialogMessage
        style="margin-bottom: 1rem"
        :type="dialogMessage.type"
        :message="dialogMessage.message"
        :visible="dialogMessage.visible" />

      <el-form ref="formRef" label-width="9rem">
        <el-form-item
          :label="i18n.t('groupAddMicrosoftForm.dataSources')"
          prop="dataSources">
          <ElSelect
            v-model="datasource.data_source_id"
            :placeholder="i18n.t('groupAddMicrosoftForm.selectDataSource')"
            filterable
            clearable
            lazy>
            <ElOption
              v-for="dataSource in connectedDataSources"
              :key="dataSource._id"
              :label="dataSource.name"
              :value="dataSource._id">
            </ElOption>
          </ElSelect>
        </el-form-item>
        <el-form-item :label="i18n.t('groups')" prop="groups">
          <ElSelectV2
            v-model="datasource.external_id"
            :options="groupSelectOptions"
            :loading="loadingGroups"
            :placeholder="i18n.t('groupAddMicrosoftForm.selectGroup')"
            :disabled="
              selectedDatasourceIsUnauthenticated ||
              !datasource.data_source_id ||
              !connectedDataSources.length ||
              availableGroups.length == 0
            "
            filterable
            clearable
            lazy
            @change="
              (v: string) =>
                emit(
                  'groupSelected',
                  groupSelectOptions.find((i) => i.value === v)?.label as string
                )
            ">
          </ElSelectV2>
        </el-form-item>
      </el-form>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import {
    AccountGroupIn,
    DataSource,
    DataSourceAvailableGroup,
    DataSourcesService,
  } from '@/client'
  import { ElSelect, ElForm } from 'element-plus'
  import { useI18n } from 'vue-i18n'
  import type { FormInstance } from 'element-plus'
  import { DialogMessageTypes } from '@/components/sm/SmDialogMessage.vue'

  const i18n = useI18n()
  const connectedDataSources = ref<DataSource[]>([])
  const availableGroups = ref<DataSourceAvailableGroup[]>([])
  const formRef = ref<FormInstance>()

  const accountGroup = defineModel<AccountGroupIn>('accountGroup', {
    required: true,
  })

  const emit = defineEmits<{
    groupSelected: [string]
  }>()

  interface AccountGroupDataSourceEdit {
    data_source_id?: string
    external_id?: string
  }

  const datasource = ref<AccountGroupDataSourceEdit>({})

  onMounted(() => {
    if (accountGroup.value.data_source) {
      datasource.value = {
        data_source_id: accountGroup.value.data_source.data_source_id,
        external_id: accountGroup.value.data_source.external_id,
      }
    }
  })

  // Selection
  const groupSyncActive = ref(accountGroup.value.data_source !== undefined)

  const groupSelectOptions = computed(() => {
    return availableGroups.value.map((group) => ({
      label: group.name,
      value: group.external_id,
    }))
  })

  // ############################
  // ####### Data Loading #######
  // ############################

  const loadingGroups = ref(false)
  const reauthDatasource = ref<DataSource>()
  const reauthDatasourceVisible = ref<boolean>(false)
  const selectedDatasourceIsUnauthenticated = ref<boolean>(false)

  function getConnectedDataSources() {
    DataSourcesService.getDataSourcesApiV1DataSourceDataSourcesGet({}).then(
      (res) => {
        connectedDataSources.value = res
      }
    )
  }

  watch(reauthDatasourceVisible, (newVal, oldVal) => {
    if (
      oldVal === true &&
      newVal === false &&
      selectedDatasourceIsUnauthenticated.value
    ) {
      datasource.value.data_source_id = undefined
      datasource.value.external_id = undefined
      selectedDatasourceIsUnauthenticated.value = false
    }
  })

  watch(
    () => datasource.value.data_source_id,
    (newDataSourceId) => {
      if (!newDataSourceId) return (datasource.value.external_id = undefined)

      checkAuthentication(newDataSourceId)
      if (!selectedDatasourceIsUnauthenticated.value) {
        getAvailableGroups()
      }
    }
  )

  watch(groupSyncActive, (newValue) => {
    if (!newValue) {
      datasource.value.data_source_id = undefined
      datasource.value.external_id = undefined
    }
  })

  onMounted(() => {
    getConnectedDataSources()
    if (accountGroup.value.data_source?.data_source_id) {
      getAvailableGroups()
    }
  })

  watch(selectedDatasourceIsUnauthenticated, (isUnauthenticated) => {
    if (isUnauthenticated) {
      const selectedDataSource = connectedDataSources.value.find(
        (ds) => ds._id === datasource.value.data_source_id
      )
      if (selectedDataSource) {
        reauthDatasource.value = selectedDataSource
        reauthDatasourceVisible.value = true
      }
    }
  })

  function checkAuthentication(id: string) {
    const selectedDataSource = connectedDataSources.value.find(
      (ds) => ds._id === id
    )
    const isExpired = selectedDataSource?.authentication_expired === true
    selectedDatasourceIsUnauthenticated.value = isExpired ? true : false
  }

  function recheckAuthentication() {
    getConnectedDataSources()

    if (datasource.value.data_source_id) {
      checkAuthentication(datasource.value.data_source_id)
    }
    if (!selectedDatasourceIsUnauthenticated.value) {
      getAvailableGroups()
    }
  }

  // ############################
  // ##### Form Validation ######
  // ############################

  const dialogMessage = ref({
    // Content of the dialog message
    message: '',
    visible: false,
    type: 'warning' as DialogMessageTypes,
  })

  function checkForm(): boolean {
    // No datasource selected
    if (groupSyncActive.value && !datasource.value.data_source_id) {
      dialogMessage.value = {
        message: i18n.t('selectDataSourceMissingError'),
        visible: true,
        type: 'warning',
      }
      return false

      // No group selected
    } else if (groupSyncActive.value && !datasource.value.external_id) {
      dialogMessage.value = {
        message: i18n.t('selectedGroupMissingError'),
        visible: true,
        type: 'warning',
      }
      return false
    }

    // Everything is fine
    return true
  }

  // Reset error message when the user changes the selected data source or group
  watch(
    () => [
      groupSyncActive.value,
      datasource.value.data_source_id && datasource.value.external_id,
    ],
    () => {
      dialogMessage.value.visible = false
    }
  )

  watch(
    datasource,
    () => {
      if (
        groupSyncActive.value &&
        datasource.value.data_source_id &&
        datasource.value.external_id
      ) {
        accountGroup.value.data_source = {
          data_source_id: datasource.value.data_source_id,
          external_id: datasource.value.external_id,
        }
      } else {
        accountGroup.value.data_source = undefined
      }
    },
    { deep: true }
  )

  defineExpose({
    formRef,
    checkForm,
  })

  // ############################
  // ####### Untils #######
  // #####################

  function getAvailableGroups(cached: boolean = true) {
    if (datasource.value.data_source_id) {
      loadingGroups.value = true
      DataSourcesService.getDataSourceAvailableGroupsApiV1DataSourceDataSourcesDataSourceIdAvailableGroupsGet(
        {
          dataSourceId: datasource.value.data_source_id,
          cached: cached,
        }
      )
        .then((response) => {
          availableGroups.value = response
          // If no cache exists in the backend --> Load unchaced
          if (cached === true && response.length === 0) {
            getAvailableGroups(false)
          }
        })
        .catch((error) => {
          console.error('Error when retrieving the groups:', error)
        })
        .finally(() => {
          loadingGroups.value = false
        })
    }
  }
</script>

<style lang="scss">
  .expand-enter-active,
  .expand-leave-active {
    overflow: hidden;
    transition:
      opacity 0.1s ease,
      max-height 0.1s ease;
  }
  .expand-enter-from,
  .expand-leave-to {
    opacity: 0;
    max-height: 0;
  }
  .expand-enter-to,
  .expand-leave-from {
    opacity: 1;
    max-height: 100px;
  }
</style>
